import { Admin, Resource, defaultTheme, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { LoginPage } from './components/LoginPage';
import { authProvider } from './authProvider';
import { dataProvider } from './dataProvider';
import { Dashboard } from './components/Dashboard';
import { AuditLogList } from './components/audit/AuditLogList';
import { LmsLogList } from './components/lms/LmsLogList';
import { LmsLogShow } from './components/lms/LmsLogShow';
import { AppTransactionsList, AppTransactionsShow } from './components/transactions/app';
import { AppTopUpsList, AppTopUpsShow, AppTopUpAttemptsList, AppTopUpAttemptsShow } from './components/app-topups';
import { AccountDeletionRequestsList, AccountDeletionRequestsShow } from './components/account-deletion-requests';
import { RfidTransactionsList, RfidTransactionsShow } from './components/transactions/rfid';
import { KioskTransactionsPage, KioskTransactionsShow } from './components/transactions/kiosk';
import { KioskSettlementsList } from './components/kiosk-settlements';
import ActiveSessionsList from './components/active-sessions/ActiveSessionsList';
import ActiveSessionsShow from './components/active-sessions/ActiveSessionsShow';
import ChargingSessionsList from './components/charging-sessions/ChargingSessionsList';
import ChargingSessionsShow from './components/charging-sessions/ChargingSessionsShow';
import SettingsList from './components/settings/SettingsList';
import { UserList, UserEdit, UserCreate } from './components/users';
import { MyProfile } from './components/users/MyProfile';
import { AppUserList, AppUserEdit } from './components/app-users';
import { AppVersionList, AppVersionEdit } from './components/app-versions';
import { SitesList, SiteEdit, SiteCreate, SitesShow } from './components/sites';
import { ChargersList, ChargersEdit, ChargersCreate, ChargersShow } from './components/chargers';
import { ChargerTypesList, ChargerTypeCreate, ChargerTypeEdit } from './components/charger-types';
import { FirmwareList, FirmwareCreate, FirmwareEdit } from './components/charger-firmware';
import { FirmwareUpdatesList, FirmwareUpdatesCreate, FirmwareUpdatesShow } from './components/firmware-updates';
import { RolesList, RoleCreate, RoleEdit } from './components/roles';
import { LoadGroupsList, LoadGroupCreate, LoadGroupEdit } from './components/load-groups';
import { LMSTemplatesList, LMSTemplatesCreate, LMSTemplatesEdit, LMSTemplatesShow } from './components/lms-templates';
import { OCPPTemplatesList, OCPPTemplatesEdit, OCPPTemplatesCreate } from './components/ocpp-templates';
import { OcppServersList, OcppServersCreate, OcppServersEdit } from './components/ocpp-servers';
import { SupabaseConnectionsList, SupabaseConnectionsCreate, SupabaseConnectionsEdit, SupabaseConnectionsShow } from './components/supabase-connections';
import { FeeTemplatesList, FeeTemplatesCreate, FeeTemplatesEdit, FeeTemplatesShow } from './components/fee-templates';
import { RfidUsersList, RfidUsersCreate, RfidUsersEdit } from './components/rfid-users';
import { VendorsList, VendorsCreate, VendorsEdit } from './components/vendors';
import { KioskDevicesList, KioskDeviceCreate, KioskDeviceEdit } from './components/kiosk-devices';
import { KioskAlertsList, KioskAlertShow } from './components/kiosk-alerts';
import { KioskReleasesList, KioskReleaseShow } from './components/kiosk-releases';
import { KioskGroupsList, KioskGroupShow } from './components/kiosk-groups';
import { ReleaseKeyRoute } from './components/kiosk-release-keys';
import { VehicleBrandsList, VehicleBrandsCreate, VehicleBrandsEdit } from './components/vehicle-brands';
import { VehicleModelsList, VehicleModelsCreate, VehicleModelsEdit } from './components/vehicle-models';
import { RfidCardsList, RfidCardsCreate, RfidCardsEdit, RfidCardsShow } from './components/rfid-cards';
import { FeeSchedulesList, FeeSchedulesCreate, FeeSchedulesEdit, FeeSchedulesShow } from './components/fee-schedules';
import { FeeTemplateChangesList, FeeTemplateChangesShow } from './components/fee-template-changes';
import { EffectivePricingViewer } from './components/fee-tools';
import CronMonitorPage from './components/system/CronMonitorPage';
import { EmailProviderRoutingPage } from './components/email-provider-routing';
import { FixedLayout } from './components/layout/FixedLayout';
import { NotificationGroups } from './components/notifications/NotificationGroups';
import { NotificationLogs } from './components/notifications/NotificationLogs';
import MessageList from './components/messages/MessageList';
import MessageCreate from './components/messages/MessageCreate';
import MessageEdit from './components/messages/MessageEdit';
import MessageShow from './components/messages/MessageShow';
import BannerList from './components/banners/BannerList';
import { BannerCreate } from './components/banners/BannerCreate';
import BannerEdit from './components/banners/BannerEdit';
import { PromotionsList } from './components/promotions/PromotionsList';
import { PromotionsCreate } from './components/promotions/PromotionsCreate';
import { PromotionsEdit } from './components/promotions/PromotionsEdit';
import { PromotionsShow } from './components/promotions/PromotionsShow';
import { PaymentMethodsList, PaymentMethodsEdit } from './components/payment-methods';
import { PaymentGatewaysList, PaymentGatewaysEdit } from './components/payment-gateways';
import { OcppLogsQueryPage } from './pages/OcppLogsQueryPage';
import { AiTroubleshootingPage } from './components/ai-troubleshooting';
import { AiFaultAnalytics } from './components/ai-troubleshooting/AiFaultAnalytics';
import { ReportsDashboard } from './components/reports/ReportsDashboard';
import { AccessDeniedPage } from './components/AccessDeniedPage';
import { RequirePermission, guard, guardAny, guardNonVendorScoped, guardPlatformOnly } from './components/RequirePermission';
import { RevenueAnalysisReport } from './components/reports/financial/RevenueAnalysisReport';
import { TransactionVolumeReport } from './components/reports/financial/TransactionVolumeReport';
import { SessionAnalyticsReport } from './components/reports/operational/SessionAnalyticsReport';
import { ChargerUtilizationReport } from './components/reports/operational/ChargerUtilizationReport';
import { EnergyConsumptionReport } from './components/reports/operational/EnergyConsumptionReport';
import { PeakUsageAnalysisReport } from './components/reports/operational/PeakUsageAnalysisReport';
import { UserActivityReport } from './components/reports/operational/UserActivityReport';
import { VendorPerformanceReport } from './components/reports/vendor/VendorPerformanceReport';
import { SiteComparisonReport } from './components/reports/vendor/SiteComparisonReport';
import { KioskReport } from './components/reports/kiosk/KioskReport';
import { createTheme } from '@mui/material/styles';
import { Security, People, ElectricBolt, DirectionsCar, Receipt, CalendarMonth, Settings, Shield, Bolt, Equalizer, AttachMoney, Schedule, Speed, Groups, History, Timeline, Storage, CloudQueue, Message, Campaign, CreditCard, LocalOffer, SystemUpdate, CloudDownload, Router, Payment, AccountBalanceWallet, PersonRemove, EmojiTransportation, CarRental, EventAvailable, Rule, Storefront } from '@mui/icons-material';
import { i18nProvider } from './i18n';
import { userGroupsResource } from './components/user-groups';
import { reservationPoliciesResource } from './components/reservation-policies';
import { reservationsResource, ReservationsCalendarView } from './components/reservations';
import { aiPlaybooksResource } from './components/ai-playbooks';
import { aiModelConfigsResource } from './components/ai-model-configs';

// Create a sophisticated, modern theme with subtle green accents
const lightTheme = createTheme({
  ...defaultTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#009A75', // Updated green color
      light: '#00BF8F',
      dark: '#007F5F',
    },
    secondary: {
      main: '#1A237E', // Deep blue for contrast
      light: '#534BAE',
      dark: '#000051',
    },
    success: {
      main: '#009A75', // Updated green color to match primary
      light: '#00BF8F',
      dark: '#007F5F',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#606060',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1A1A1A',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          borderBottom: '1px solid #F0F0F0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,154,117,0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.06)',
          transition: 'none',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            transform: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#FAFAFA',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
            },
          },
        },
      },
    },
  },
});


function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      loginPage={LoginPage}
      dashboard={Dashboard}
      layout={FixedLayout}
      theme={lightTheme}
      requireAuth={false}
      disableTelemetry
    >
      <CustomRoutes>
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/ocpp-logs" element={<OcppLogsQueryPage />} />
        <Route
          path="/ai-troubleshooting"
          element={
            <RequirePermission permission="ai_troubleshooting:use:all">
              <AiTroubleshootingPage />
            </RequirePermission>
          }
        />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/kiosk-release-key" element={<ReleaseKeyRoute />} />
        <Route
          path="/reservations/calendar"
          element={
            <RequirePermission permission="reservations:read:all">
              <ReservationsCalendarView />
            </RequirePermission>
          }
        />
      </CustomRoutes>
      <Resource
        name="audit-logs"
        list={guard('audit:read:all', AuditLogList)}
        icon={Security}
      />
      <Resource
        name="backend-users"
        list={guard('users:read:all', UserList)}
        edit={guardAny(['users:update:all', 'users:write:all'], UserEdit)}
        create={guardAny(['users:create:all', 'users:write:all'], UserCreate)}
        icon={People}
      />
      <Resource
        name="roles"
        list={guard('roles:read:all', RolesList)}
        create={guard('roles:write:all', RoleCreate)}
        edit={guard('roles:write:all', RoleEdit)}
        icon={Shield}
      />
      <Resource
        name="sites"
        list={guard('sites:read:all', SitesList)}
        show={guard('sites:read:all', SitesShow)}
        edit={guard('sites:write:all', SiteEdit)}
        create={guard('sites:write:all', SiteCreate)}
        icon={ElectricBolt}
      />
      <Resource
        name="load-groups"
        list={guard('load-groups:read:all', LoadGroupsList)}
        edit={guard('load-groups:write:all', LoadGroupEdit)}
        create={guard('load-groups:write:all', LoadGroupCreate)}
        icon={Bolt}
      />
      <Resource
        name="lms-templates"
        list={guard('lms-templates:read:all', LMSTemplatesList)}
        edit={guard('lms-templates:write:all', LMSTemplatesEdit)}
        create={guard('lms-templates:write:all', LMSTemplatesCreate)}
        show={guard('lms-templates:read:all', LMSTemplatesShow)}
        icon={Equalizer}
        recordRepresentation="name"
      />
      <Resource
        name="lms-logs"
        list={guard('lms-logs:read:all', LmsLogList)}
        show={guard('lms-logs:read:all', LmsLogShow)}
        icon={Timeline}
      />
      <Resource
        name="ocpp-templates"
        list={guard('ocpp-templates:read:all', OCPPTemplatesList)}
        edit={guard('ocpp-templates:write:all', OCPPTemplatesEdit)}
        create={guard('ocpp-templates:write:all', OCPPTemplatesCreate)}
        icon={Speed}
        recordRepresentation="templateName"
      />
      <Resource
        name="ocpp-servers"
        list={guard('ocpp-servers:read:all', OcppServersList)}
        create={guard('ocpp-servers:write:all', OcppServersCreate)}
        edit={guard('ocpp-servers:write:all', OcppServersEdit)}
        icon={Storage}
      />
      <Resource
        name="supabase-connections"
        list={guard('supabase-connections:read:all', SupabaseConnectionsList)}
        create={guard('supabase-connections:write:all', SupabaseConnectionsCreate)}
        edit={guard('supabase-connections:write:all', SupabaseConnectionsEdit)}
        show={guard('supabase-connections:read:all', SupabaseConnectionsShow)}
        icon={CloudQueue}
        recordRepresentation="name"
      />
      <Resource
        name="chargers"
        list={guard('chargers:read:all', ChargersList)}
        show={guard('chargers:read:all', ChargersShow)}
        edit={guard('chargers:write:all', ChargersEdit)}
        create={guard('chargers:write:all', ChargersCreate)}
        icon={DirectionsCar}
      />
      <Resource
        name="charger-types"
        list={guard('charger-types:read:all', ChargerTypesList)}
        edit={guard('charger-types:write:all', ChargerTypeEdit)}
        create={guard('charger-types:write:all', ChargerTypeCreate)}
        icon={Bolt}
        recordRepresentation="displayName"
      />
      <Resource
        name="vehicle-brands"
        list={guard('vehicle-catalog:read:all', VehicleBrandsList)}
        create={guard('vehicle-catalog:write:all', VehicleBrandsCreate)}
        edit={guard('vehicle-catalog:write:all', VehicleBrandsEdit)}
        icon={EmojiTransportation}
        recordRepresentation="name"
        options={{ label: 'Vehicle Brands' }}
      />
      <Resource
        name="vehicle-models"
        list={guard('vehicle-catalog:read:all', VehicleModelsList)}
        create={guard('vehicle-catalog:write:all', VehicleModelsCreate)}
        edit={guard('vehicle-catalog:write:all', VehicleModelsEdit)}
        icon={CarRental}
        recordRepresentation="name"
        options={{ label: 'Vehicle Models' }}
      />
      <Resource
        name="charger-firmware"
        list={guard('charger-firmware:read:all', FirmwareList)}
        create={guard('charger-firmware:write:all', FirmwareCreate)}
        edit={guard('charger-firmware:write:all', FirmwareEdit)}
        icon={CloudDownload}
        options={{ label: 'Charger Firmware' }}
      />
      <Resource
        name="firmware-updates"
        list={guard('firmware-updates:read:all', FirmwareUpdatesList)}
        create={guard('firmware-updates:write:all', FirmwareUpdatesCreate)}
        show={guard('firmware-updates:read:all', FirmwareUpdatesShow)}
        icon={SystemUpdate}
        options={{ label: 'Firmware Updates' }}
      />
      <Resource
        name="fee-templates"
        list={guard('fee-templates:read:all', FeeTemplatesList)}
        create={guard('fee-templates:write:all', FeeTemplatesCreate)}
        edit={guard('fee-templates:write:all', FeeTemplatesEdit)}
        show={guard('fee-templates:read:all', FeeTemplatesShow)}
        icon={AttachMoney}
        options={{ label: 'Fee Templates' }}
      />
      <Resource
        name="fee-template-changes"
        list={guard('fee-templates:read:all', FeeTemplateChangesList)}
        show={guard('fee-templates:read:all', FeeTemplateChangesShow)}
        icon={Schedule}
        options={{ label: 'Scheduled Fee Changes' }}
      />
      <Resource
        name="fee-schedules"
        list={guard('fee-schedules:read:all', FeeSchedulesList)}
        create={guard('fee-schedules:write:all', FeeSchedulesCreate)}
        edit={guard('fee-schedules:write:all', FeeSchedulesEdit)}
        show={guard('fee-schedules:read:all', FeeSchedulesShow)}
        icon={Schedule}
        options={{ label: 'Fee Schedules' }}
      />
      <CustomRoutes>
        <Route path="/pricing-viewer" element={<EffectivePricingViewer />} />
        <Route
          path="/cron-monitor"
          element={
            <RequirePermission permission="system:admin:all">
              <CronMonitorPage />
            </RequirePermission>
          }
        />
        <Route
          path="/email-provider-routing"
          element={
            <RequirePermission permission="email_routing:read:all">
              <EmailProviderRoutingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ai-troubleshooting/analytics"
          element={
            <RequirePermission permission="ai_troubleshooting:manage:all">
              <AiFaultAnalytics />
            </RequirePermission>
          }
        />
        <Route path="/reports" element={<ReportsDashboard />} />
        <Route path="/reports/financial/revenue" element={<RevenueAnalysisReport />} />
        <Route path="/reports/financial/transactions" element={<TransactionVolumeReport />} />
        <Route
          path="/reports/financial/kiosk"
          element={
            <RequirePermission permission="reports:kiosk:read">
              <KioskReport />
            </RequirePermission>
          }
        />
        <Route path="/reports/operational/sessions" element={<SessionAnalyticsReport />} />
        <Route path="/reports/operational/charger-utilization" element={<ChargerUtilizationReport />} />
        <Route path="/reports/operational/energy-consumption" element={<EnergyConsumptionReport />} />
        <Route path="/reports/operational/peak-usage" element={<PeakUsageAnalysisReport />} />
        <Route path="/reports/operational/user-activity" element={<UserActivityReport />} />
        <Route path="/reports/vendor/performance" element={<VendorPerformanceReport />} />
        <Route path="/reports/vendor/sites" element={<SiteComparisonReport />} />
      </CustomRoutes>
      <Resource
        name="app-users"
        list={guard('app-users:read:all', AppUserList)}
        edit={guard('app-users:write:all', AppUserEdit)}
        icon={People}
        options={{ label: 'App Users' }}
      />
      <Resource
        name="app-versions"
        list={guard('app-versions:read:all', AppVersionList)}
        edit={guard('app-versions:write:all', AppVersionEdit)}
        icon={SystemUpdate}
        options={{ label: 'App Versions' }}
      />
      <Resource
        name="account-deletion-requests"
        list={guard('account_deletion_requests:process:all', AccountDeletionRequestsList)}
        show={guard('account_deletion_requests:process:all', AccountDeletionRequestsShow)}
        icon={PersonRemove}
        options={{ label: 'App Account Deletion' }}
      />
      <Resource
        name="vendors"
        list={guard('vendors:read:all', VendorsList)}
        create={guard('vendors:write:all', VendorsCreate)}
        edit={guard('vendors:write:all', VendorsEdit)}
        icon={Groups}
      />
      <Resource
        name="kiosk-devices"
        list={guard('kiosk-devices:read:all', KioskDevicesList)}
        create={guard('kiosk-devices:write:all', KioskDeviceCreate)}
        edit={guard('kiosk-devices:write:all', KioskDeviceEdit)}
        icon={Storefront}
      />
      <Resource
        name="kiosk-alerts"
        list={guard('kiosk-alerts:read:all', KioskAlertsList)}
        show={guard('kiosk-alerts:read:all', KioskAlertShow)}
        icon={Storefront}
        options={{ label: 'Kiosk alerts' }}
      />
      <Resource
        name="kiosk-releases"
        list={guardPlatformOnly('kiosk-releases:read:all', KioskReleasesList)}
        show={guardPlatformOnly('kiosk-releases:read:all', KioskReleaseShow)}
        icon={Storefront}
        options={{ label: 'Kiosk releases' }}
      />
      <Resource
        name="kiosk-groups"
        list={guardPlatformOnly('kiosk-releases:read:all', KioskGroupsList)}
        show={guardPlatformOnly('kiosk-releases:read:all', KioskGroupShow)}
      />
      <Resource
        name="rfid-users"
        list={guard('rfid-users:read:all', RfidUsersList)}
        create={guard('rfid-users:write:all', RfidUsersCreate)}
        edit={guard('rfid-users:write:all', RfidUsersEdit)}
        icon={People}
      />
      <Resource
        name="rfid-cards"
        list={guard('rfid-cards:read:all', RfidCardsList)}
        create={guard('rfid-cards:write:all', RfidCardsCreate)}
        edit={guard('rfid-cards:write:all', RfidCardsEdit)}
        show={guard('rfid-cards:read:all', RfidCardsShow)}
        recordRepresentation="cardNumber"
        icon={CreditCard}
      />
      <Resource
        name="messages"
        list={guard('messages:read:all', MessageList)}
        create={guard('messages:write:all', MessageCreate)}
        edit={guard('messages:write:all', MessageEdit)}
        show={guard('messages:read:all', MessageShow)}
        icon={Message}
        options={{ label: 'Messages' }}
      />
      <Resource
        name="banners"
        // Banners point at /api/backend/promotion-banners, which is gated on
        // `messages:*:all` (not `banners:*:all`) — match the backend so a user
        // with messages perms isn't blocked at the UI gate.
        list={guard('messages:read:all', BannerList)}
        create={guard('messages:write:all', BannerCreate)}
        edit={guard('messages:write:all', BannerEdit)}
        icon={Campaign}
        options={{ label: 'Banners' }}
      />
      <Resource
        name="promotions"
        list={guard('promotions:read:all', PromotionsList)}
        create={guard('promotions:write:all', PromotionsCreate)}
        edit={guard('promotions:write:all', PromotionsEdit)}
        show={guard('promotions:read:all', PromotionsShow)}
        icon={LocalOffer}
        options={{ label: 'Promotions' }}
      />
      <Resource
        name="app-transactions"
        list={guardNonVendorScoped('app-transactions:read:all', AppTransactionsList)}
        show={guardNonVendorScoped('app-transactions:read:all', AppTransactionsShow)}
        icon={Receipt}
        options={{ label: 'App Transactions' }}
      />
      <Resource
        name="app-topups"
        list={guard('app-topups:read:all', AppTopUpsList)}
        show={guard('app-topups:read:all', AppTopUpsShow)}
        icon={AccountBalanceWallet}
        options={{ label: 'Top-Ups' }}
      />
      <Resource
        name="app-topup-attempts"
        list={guard('app-topups:read:all', AppTopUpAttemptsList)}
        show={guard('app-topups:read:all', AppTopUpAttemptsShow)}
        icon={AccountBalanceWallet}
        options={{ label: 'Top-Up Attempts' }}
      />
      <Resource
        name="rfid-transactions"
        list={guard('rfid-transactions:read:all', RfidTransactionsList)}
        show={guard('rfid-transactions:read:all', RfidTransactionsShow)}
        icon={CreditCard}
        options={{ label: 'RFID Transactions' }}
      />
      <Resource
        name="kiosk-transactions"
        list={guard('kiosk-transactions:read:all', KioskTransactionsPage)}
        show={guard('kiosk-transactions:read:all', KioskTransactionsShow)}
        icon={Storefront}
        options={{ label: 'Kiosk Transactions' }}
      />
      <Resource
        name="kiosk-settlements"
        list={guard('kiosk-settlements:read:all', KioskSettlementsList)}
        icon={Storefront}
        options={{ label: 'Octopus settlements' }}
      />
      <Resource
        name="active-sessions"
        list={guard('active-sessions:read:all', ActiveSessionsList)}
        show={guard('active-sessions:read:all', ActiveSessionsShow)}
        icon={Bolt}
        options={{ label: 'Active Sessions' }}
      />
      <Resource
        name="charging-sessions"
        list={guard('charging-sessions:read:all', ChargingSessionsList)}
        show={guard('charging-sessions:read:all', ChargingSessionsShow)}
        icon={Bolt}
        options={{ label: 'Charging History' }}
      />
      <Resource name="user-groups" {...userGroupsResource} />
      <Resource name="reservation-policies" {...reservationPoliciesResource} />
      <Resource name="reservations" {...reservationsResource} />
      <Resource name="ai-playbooks" {...aiPlaybooksResource} />
      <Resource name="ai-model-configs" {...aiModelConfigsResource} />
      <Resource
        name="notification-groups"
        list={guard('notifications:read:all', NotificationGroups.list)}
        create={guard('notifications:write:all', NotificationGroups.create)}
        edit={guard('notifications:write:all', NotificationGroups.edit)}
        icon={Groups}
        options={{ label: 'Notification Groups' }}
      />
      <Resource
        name="notification-logs"
        list={guard('notifications:read:all', NotificationLogs.list)}
        show={guard('notifications:read:all', NotificationLogs.show)}
        icon={History}
        options={{ label: 'Notification Logs' }}
      />
      <Resource
        name="payment-methods"
        list={guard('payment-settings:read:all', PaymentMethodsList)}
        edit={guard('payment-settings:write:all', PaymentMethodsEdit)}
        icon={Payment}
        options={{ label: 'Payment Methods' }}
      />
      <Resource
        name="payment-gateways"
        list={guard('payment-settings:read:all', PaymentGatewaysList)}
        edit={guard('payment-settings:write:all', PaymentGatewaysEdit)}
        icon={AccountBalanceWallet}
        options={{ label: 'Payment Gateways' }}
      />
      <Resource
        name="settings"
        list={guard('settings:read:all', SettingsList)}
        icon={Settings}
      />
    </Admin>
  );
}

export default App;
